import { useDataProvider, useNotify } from "ra-core";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { CLIENT_STAGES, labelOf } from "./gulaConstants";
import type { GulaClient } from "./gulaTypes";
import { StageChangeDialog, type PendingMove } from "./StageChangeDialog";

/** Moves a setter can close a call with ("No contesta" has its own button). */
const RESULT_STAGES = [
  "first_contact",
  "second_contact",
  "recontact",
  "sure_purchase",
  "no_purchase",
  "purchased",
];

/** Moves that ask for more data first (date, closing note, reason, order). */
const STAGES_WITH_DIALOG = [
  "recontact",
  "sure_purchase",
  "no_purchase",
  "purchased",
];

const STAGE_STYLE: Record<string, string> = {
  recontact: "hover:border-sky-400 hover:bg-sky-50 dark:hover:bg-sky-400/15",
  sure_purchase:
    "hover:border-lime-500 hover:bg-lime-50 dark:hover:bg-lime-400/15",
  no_purchase:
    "hover:border-red-300 hover:bg-red-50 dark:hover:border-red-400 dark:hover:bg-red-400/15",
  purchased:
    "border-lime-500 bg-lime-400 font-semibold text-neutral-900 hover:bg-lime-300",
};

/**
 * "Resultado de la llamada": moves the lead to its new phase from the client
 * panel, same as dragging its card on the Clientes board (the call counts and
 * the call of today's list gets crossed out by itself).
 */
export const ClientStageBar = ({
  client,
  onChanged,
}: {
  client: GulaClient;
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [pending, setPending] = useState<PendingMove>(null);
  const [busy, setBusy] = useState(false);

  const move = async (toStage: string) => {
    if (toStage === client.stage) return;
    if (STAGES_WITH_DIALOG.includes(toStage)) {
      setPending({ client, toStage });
      return;
    }
    setBusy(true);
    try {
      await dataProvider.update("gula_clients", {
        id: client.id,
        data: { stage: toStage },
        previousData: { id: client.id },
      });
      notify(
        `${client.first_name ?? "Lead"} pasa a ${labelOf(CLIENT_STAGES, toStage)}`,
        { type: "success" },
      );
      onChanged();
    } catch (error) {
      console.error(error);
      notify("No se ha podido mover", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-2 rounded-2xl border bg-white p-3 dark:bg-card">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Resultado de la llamada</h4>
        <span className="text-xs text-muted-foreground">
          Ahora en{" "}
          <strong className="text-foreground">
            {labelOf(CLIENT_STAGES, client.stage)}
          </strong>
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RESULT_STAGES.map((stage) => {
          const current = stage === client.stage;
          return (
            <button
              key={stage}
              type="button"
              disabled={busy || current}
              onClick={() => move(stage)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm transition-colors disabled:cursor-default",
                current
                  ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                  : (STAGE_STYLE[stage] ?? "hover:bg-muted"),
                busy && !current && "opacity-50",
              )}
            >
              {labelOf(CLIENT_STAGES, stage)}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Al moverlo cuenta la llamada y se tacha de tu lista.
      </p>
      <StageChangeDialog
        pending={pending}
        onClose={() => setPending(null)}
        onDone={() => {
          setPending(null);
          onChanged();
        }}
      />
    </section>
  );
};
